'use client';

import { AlertCircle, CheckCircle2, RefreshCw, Upload, X } from 'lucide-react';

import { formatBytes, formatSpeed, formatTimeLeft } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import { Card } from '@kit/ui/card';
import { If } from '@kit/ui/if';
import { Progress } from '@kit/ui/progress';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import {
  UploadFile,
  UploadFileResult,
  useFileUploader,
} from './use-file-uploader';

/**
 * FileUploader component displays the list of files being uploaded
 * with their progress, status, and controls.
 */
export default function FileUploader() {
  const { files } = useFileUploader();

  if (files.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex w-full flex-col gap-2 border-none">
        {files
          .filter((file) => file.status === 'uploading')
          .map((fileData) => (
            <FileUploadItem key={fileData.id} fileData={fileData} />
          ))}
      </Card>
    </div>
  );
}

/**
 * Returns the appropriate status icon based on file status
 */
const getStatusIcon = (fileData: UploadFile) => {
  switch (fileData.status) {
    case 'completed':
      return <CheckCircle2 size={16} className="text-green-500" />;
    case 'uploading':
      return <Upload size={16} className="animate-pulse text-blue-500" />;
    case 'paused':
      return <RefreshCw size={16} className="text-amber-500" />;
    case 'error':
      return <AlertCircle size={16} className="text-destructive" />;
    default:
      return null;
  }
};

/**
 * Returns the status text based on file status
 */
const getStatusText = (fileData: UploadFile) => {
  switch (fileData.status) {
    case 'completed':
      return 'Completed';
    case 'uploading':
      return 'Uploading...';
    case 'paused':
      return `Paused (click to resume) ${fileData.progress}%`;
    case 'error':
      return 'Failed';
    case 'pending':
      return '';
    default:
      return '';
  }
};

export const FileUploadItem = ({
  fileData,
  onComplete,
}: {
  fileData: UploadFile;
  onComplete?: (result: UploadFileResult) => void;
}) => {
  const { uploadFile, removeFile, resumeUpload } = useFileUploader();

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border p-2">
      {/* File info and controls */}
      <div className="flex flex-col space-y-1">
        {/* File name and status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 overflow-hidden">
            {getStatusIcon(fileData)}
            <span
              className="max-w-[200px] truncate text-sm font-medium"
              title={fileData.fileName}
            >
              {fileData.fileName}
            </span>
          </div>

          <div className="flex items-center gap-1">
            {/* Resume button for paused or error files with uploadId */}
            {(fileData.status === 'pending' ||
              fileData.status === 'paused' ||
              (fileData.status === 'error' && fileData.uploadId)) && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() =>
                  fileData.status === 'paused' || fileData.status === 'error'
                    ? resumeUpload(fileData, onComplete)
                    : uploadFile(fileData, onComplete)
                }
              >
                {fileData.status === 'paused' || fileData.status === 'error'
                  ? 'Resume'
                  : 'Upload'}
              </Button>
            )}

            {/* Remove file button */}
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={() => removeFile(fileData.id)}
            >
              <X size={14} />
            </Button>
          </div>
        </div>

        {/* File details */}
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span>{formatBytes(fileData.fileSize)}</span>
            <span>{getStatusText(fileData)}</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Display upload speed for uploading files */}
            {fileData.status === 'uploading' && fileData.uploadSpeed > 0 && (
              <span className="text-xs font-medium text-blue-500">
                {formatSpeed(fileData.uploadSpeed)}
              </span>
            )}

            {/* Display ETA for uploading files */}
            {fileData.status === 'uploading' &&
              fileData.eta !== undefined &&
              fileData.eta > 0 && (
                <div className="flex items-center justify-end">
                  <span className="text-muted-foreground text-xs">
                    <Trans i18nKey="views:uploadManager.eta" />{' '}
                    <span className="font-medium text-blue-500">
                      {formatTimeLeft(fileData.eta)}
                    </span>
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      <If condition={fileData.status === 'uploading'}>
        <div className="space-y-1">
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              <Trans i18nKey="views:uploadManager.progress" />
            </span>
            <span>{fileData.progress}%</span>
          </div>
          <Progress
            value={fileData.progress}
            className={cn(
              'h-2',
              fileData.status === 'error' && 'bg-destructive/20',
              fileData.status === 'paused' && 'bg-amber-200/50',
            )}
          />
        </div>
      </If>

      {/* Chunk progress - only show when uploading multiple chunks */}
      {fileData.status === 'uploading' && fileData.totalChunks > 1 && (
        <div className="space-y-1">
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              <Trans i18nKey="views:uploadManager.chunk" />{' '}
              {fileData.currentChunk}
              <Trans i18nKey="views:uploadManager.of" />
              {fileData.totalChunks}
            </span>
            <span>{fileData.chunkProgress}%</span>
          </div>
          <Progress value={fileData.chunkProgress} className="h-1.5" />
        </div>
      )}

      {/* Error message */}
      {fileData.error && (
        <div className="text-destructive mt-1 flex items-start gap-1 text-xs">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">{fileData.error}</span>
        </div>
      )}
    </div>
  );
};
